import { useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ChevronDown, X } from 'lucide-react';

interface MultiSelectDropdownProps {
	label: string;
	options: string[];
	selected: string[];
	onChange: (selected: string[]) => void;
	className?: string;
	itemClassName?: string;
	disabled?: boolean;
}

export function MultiSelectDropdown({
	label,
	options,
	selected,
	onChange,
	className,
	itemClassName,
	disabled = false,
}: MultiSelectDropdownProps) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);

	const toggle = (option: string, checked: boolean) => {
		onChange(checked ? [...selected, option] : selected.filter((v) => v !== option));
	};

	const remove = (option: string, e: React.MouseEvent) => {
		e.stopPropagation();
		onChange(selected.filter((v) => v !== option));
	};

	return (
		<Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
			<PopoverTrigger asChild>
				{/* input-style trigger; width matches trigger for popover alignment */}
				<div
					ref={triggerRef}
					role="combobox"
					aria-expanded={open}
					aria-haspopup="listbox"
					aria-disabled={disabled}
					tabIndex={disabled ? -1 : 0}
					onKeyDown={(e) => {
						if (!disabled && (e.key === 'Enter' || e.key === ' ')) setOpen((o) => !o);
					}}
					className={cn(
						'flex min-h-9 w-48 cursor-pointer items-start gap-1 rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
						disabled && 'cursor-not-allowed opacity-50',
						className,
					)}
				>
					{/* two-line cap; badges scroll inside without growing the trigger */}
					<div className="flex max-h-[4.25rem] min-w-0 flex-1 flex-wrap gap-1 overflow-y-auto">
						{selected.length === 0 ? (
							<span className="text-muted-foreground">Select {label}...</span>
						) : (
							selected.map((item) => (
								<Badge key={item} variant="secondary" className={cn('gap-1 pr-1', itemClassName)}>
									{item}
									<button
										type="button"
										aria-label={`Remove ${item}`}
										onClick={(e) => remove(item, e)}
										className="rounded-sm opacity-60 hover:opacity-100 focus:outline-none"
									>
										<X className="h-3 w-3" />
									</button>
								</Badge>
							))
						)}
					</div>
					<ChevronDown
						className={cn('ml-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
					/>
				</div>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="p-1"
				// match the trigger width dynamically
				style={{ width: triggerRef.current?.offsetWidth }}
			>
				{options.length === 0 ? (
					<p className="px-2 py-1.5 text-sm text-muted-foreground">No options available.</p>
				) : (
					<ul role="listbox" className="flex flex-col gap-0.5">
						{options.map((option) => (
							<li key={option}>
								<label
									className={cn(
										'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground',
										itemClassName,
									)}
								>
									<Checkbox
										checked={selected.includes(option)}
										onCheckedChange={(checked) => toggle(option, !!checked)}
									/>
									{option}
								</label>
							</li>
						))}
					</ul>
				)}
			</PopoverContent>
		</Popover>
	);
}